import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 if NOT on login/signup/signup-status pages
    const authEndpoints = ['/auth/login', '/auth/signup', '/admin/signup-status'];
    const isAuthEndpoint = authEndpoints.some(endpoint => 
      error.config?.url?.includes(endpoint)
    );
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Wallet API
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  addMoney: (amount) => api.post('/wallet/add-money', { amount }),
  getTransactions: () => api.get('/wallet/transactions'),
  getPaymentStatus: (orderNo) => api.get(`/wallet/payment-status/${orderNo}`),
  getCommission: () => api.get('/wallet/commission'),
};

// Withdrawal API
export const withdrawalAPI = {
  request: (data) => api.post('/withdrawal/request', data),
  getHistory: () => api.get('/withdrawal/history'),
  getDetails: (withdrawalId) => api.get(`/withdrawal/${withdrawalId}`),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: (filters = {}) => api.get('/admin/users', { params: filters }),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  updateUserData: (userId, data) => api.put(`/admin/users/${userId}`, data),
  restrictUser: (userId, data) => api.post(`/admin/users/${userId}/restrict`, data),
  freezeUser: (userId) => api.post(`/admin/users/${userId}/freeze`),
  activateUser: (userId) => api.post(`/admin/users/${userId}/activate`),
  
  // Commission Settings
  getCommissionSettings: (filters = {}) => api.get('/admin/commission-settings', { params: filters }),
  updateCommissionSettings: (data) => api.post('/admin/commission-settings', data),
  getCommissionSettingsAll: () => api.get('/admin/commission-settings-all'),
  getDefaultCommissions: () => api.get('/admin/default-commissions'),
  setDefaultCommissions: (data) => api.post('/admin/default-commissions', data),
  
  // Referral Earnings
  getPendingReferrals: () => api.get('/admin/referrals/pending'),
  getUserReferrals: (userId, filters = {}) => api.get(`/admin/referrals/user/${userId}`, { params: filters }),
  creditReferralEarnings: (data) => api.post('/admin/referrals/credit', data),
  
  // Payment Gateway Configuration
  getPaymentConfig: () => api.get('/admin/payment-config'),
  updatePaymentConfig: (data) => api.post('/admin/payment-config', data),
  
  // Database Export/Import
  exportDatabase: () => api.get('/admin/database/export'),
  importDatabase: (data) => api.post('/admin/database/import', data),
  
  // Signup Status
  getSignupStatus: () => api.get('/admin/signup-status'),
  toggleSignupPause: (pause) => api.post('/admin/toggle-signup-pause', { pause }),
  
  // User Account Management
  freezeUser: (userId) => api.post(`/admin/users/${userId}/freeze`),
  unfreezeUser: (userId) => api.post(`/admin/users/${userId}/unfreeze`),
  terminateUser: (userId) => api.post(`/admin/users/${userId}/terminate`),
  unterminateUser: (userId) => api.post(`/admin/users/${userId}/unterminate`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  pauseWithdrawal: (userId) => api.post(`/admin/users/${userId}/pause-withdrawal`),
  resumeWithdrawal: (userId) => api.post(`/admin/users/${userId}/resume-withdrawal`),
  fetchBalance: () => api.post('/admin/fetch-balance'),
  
  // Statistics
  getStats: () => api.get('/admin/stats'),
  getLimits: () => api.get('/admin/limits'),
  updateLimits: (data) => api.post('/admin/limits', data),
  getUserTransactions: (userId) => api.get(`/admin/transactions/${userId}`),
};

export default api;
